import axios from "axios";
import ApplicationCredentials from "@/credential/application-credentials";
import store from "@/store/index.js"
import TagsStorage from "@/tags-storage.js"

export default class BackendClient {

    constructor() {
        this.applicationCredentials = new ApplicationCredentials();
        this.tagsStorage = new TagsStorage();
    }

    getPublicLinkByOwnerAndId(owner, id) {
            let url = `${process.env.VUE_APP_BACKEND_URL}/public-link/${owner}/${id}`

            return this.withForbiddenErrorHandling(axios({
                method: 'get',
                url: url
            }).then(response => {
                const responseData = response.data;
                return {
                        link : this.mapSingleLink(responseData.link),
                        owner: responseData.owner
                    };
            }));
        }

    login(userName, password) {
        const url = `${process.env.VUE_APP_BACKEND_URL}/login`;
        return axios({
              method: "post",
              url: url,
              params: {username: userName, password: password}
            })
            .then(result => {
                const auth = result.headers.authorization;
                store.commit('loginUser', { auth, userName });
                store.dispatch('showPaymentNotice');
            })
            .catch(error => {
                if (error?.response?.status === 401) {
                    throw new Error("Invalid user name or password");
                }
                throw error;
            });
    }
    getLinks(owner, tag, page) {
        let url = `${process.env.VUE_APP_BACKEND_URL}/links/${owner}/`
        if (tag) {
            url += `tag/${tag}/`;
        }

        if (page) {
            url += `${page}/`;
        }

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: url,
            headers: this.getAuthHeader()
        }).then(response => this.mapResponseData(response)));
    }

    search(owner, searchTerm) {
        const url = `${process.env.VUE_APP_BACKEND_URL}/filteredlinks/${owner}/`

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            params: {searchTerm: searchTerm},
            url: url,
            headers: this.getAuthHeader()
        }).then(response => {
            return {
                links: this.mapLinks(response.data.links),
                owner: owner
            };
        }));
    }

    discover() {
        const url = `${process.env.VUE_APP_BACKEND_URL}/discover`
        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: url,
        }).then(response => {
            return {
                links: this.mapLinks(response.data.links),
            };
        }));
    }

    fetchExternalMetadata(externalUrl) {
        const url = `${process.env.VUE_APP_BACKEND_URL}/external/metadata/`

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            params: {url: externalUrl},
            url: url,
            headers: this.getAuthHeader()
        })
        .then(response => {
            return {
                title: response.data.title,
                suggestedTags: response.data.suggestedTags,
                previewImageUrl: response.data.previewImageUrl
            }
        })
        .catch(error => {
           if (error.response.status === 400) {
               store.commit("addError", "Unable to load webpage metadata!");
               return {title: "", suggestedTags: []}
           }
        })
       );
    }

    mapLinks(links) {
       return links.map((link) => this.mapSingleLink(link));
    }

    mapSingleLink(link) {
        return {
                   addedOn: link.addedOn,
                   description: link.description,
                   id: link.id,
                   owner: link.ownerName,
                   tags: this.orderedTags(link.tags),
                   url: link.url,
                   visibility: link.visibility,
                   previewImageUrl: link.previewImageUrl
                };
    }

    orderedTags(tags) {
        return tags.toSorted((a, b) => a.length - b.length);
    }

    mapResponseData(response) {
        const responseData = response.data;
        return {
                links : this.mapLinks(responseData.links),
                nextPageNumber: responseData.nextPageNumber,
                owner: responseData.owner,
                prevPageNumber: responseData.prevPageNumber
            };
    }

    saveLink(url, title, tags, visibility, previewImageUrl) {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/link/`;
        return this.withForbiddenErrorHandling(axios({
            method: 'post',
            url: backendUrl,
            data: {url, title, tags, visibility, previewImageUrl},
            headers: this.getAuthHeader()
        }))
    }

    setLinkVisibility(id, visibility) {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/link/${id}/visibility/${visibility}`;
        return this.withForbiddenErrorHandling(axios({
            method: 'put',
            url: backendUrl,
            headers: this.getAuthHeader()
        }));
    }

    updateLink(id, url, title, tags, visibility) {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/link/${id}`;
        return this.withForbiddenErrorHandling(axios({
            method: 'put',
            url: backendUrl,
            data: {url, title, tags, visibility},
            headers: this.getAuthHeader()
        }))
    }

    getAuthHeader() {
        const creds = this.applicationCredentials.loadCredentials();
        if (!creds) {
            return null;
        }
        const bearerToken = creds.token;
        return {
            Authorization: bearerToken
        };
    }

    getTagCounts() {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/tags/counts`;
        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: backendUrl,
            headers: this.getAuthHeader()
        })).then(response => {
            this.tagsStorage.saveTags(response.data);
            store.commit('loadTags');
            return response;
        });
    }

    refreshTagCountsIfNeeded() {
        if (this.tagsStorage.areTagsExpired()) {
            return this.getTagCounts();
        } else {
            return Promise.resolve();
        }
    }

    deleteLink(linkId) {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/link/${linkId}`;
        return axios({
            method: 'delete',
            url: backendUrl,
            headers: this.getAuthHeader()
        })
    }

    registerEmail(email) {
        const backendUrl = `${process.env.VUE_APP_BACKEND_URL}/register-email/`;
        return axios({
            method: 'post',
            url: backendUrl,
            data: {email}
        });
    }

    registerUser(username, email, password, regCode) {
        let url = `${process.env.VUE_APP_BACKEND_URL}/register-user`;

        return this.withForbiddenErrorHandling(axios({
            method: 'post',
            url: url,
            data: {username, email, password, regCode},
        }));
    }

    getPresignedImportUrl(importType) {
        const url = `${process.env.VUE_APP_BACKEND_URL}/gcs-upload/url/`

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            params: {import_type: importType},
            url: url,
            headers: this.getAuthHeader()
        })).then(response => { return response.data });
    }

    getJobStatus(gcsFilePath) {

        const url = `${process.env.VUE_APP_BACKEND_URL}/job/`

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            params: {file_path: gcsFilePath},
            url: url,
            headers: this.getAuthHeader()
        })).then(response => { return response.data });
    }

    getLinksByImportJobId(importJobId, page) {
        let url = `${process.env.VUE_APP_BACKEND_URL}/imported-links/${importJobId}/`;
        if (page) {
            url += `${page}/`;
        }

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: url,
            headers: this.getAuthHeader()
        }).then(response => this.mapResponseData(response)));
    }

    getImports() {
        let url = `${process.env.VUE_APP_BACKEND_URL}/jobs/`;

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: url,
            headers: this.getAuthHeader()
        })).then(response => { return response.data });
    }

    registerWaitlistUser(username, email, password, regCode) {
        let url = `${process.env.VUE_APP_BACKEND_URL}/register`;

        return this.withForbiddenErrorHandling(axios({
            method: 'post',
            url: url,
            data: {username, email, password, regCode},
        }));
    }

    getUserProfile(){
        let url = `${process.env.VUE_APP_BACKEND_URL}/user/profile`;

        return this.withForbiddenErrorHandling(axios({
            method: 'get',
            url: url,
            headers: this.getAuthHeader()
        }));
    }

    startCheckout() {
        let url = `${process.env.VUE_APP_BACKEND_URL}/payment/checkout`;

        return this.withForbiddenErrorHandling(axios({
            method: 'post',
            url: url,
            headers: this.getAuthHeader()
        }));
    }

    withForbiddenErrorHandling(promise) {
        return promise.catch(error => {
           if (error.response.status === 403) {
               store.commit("logoutUser");
               throw new Error("Your login has been expired, please sign in again!")
           }
           if (error?.response?.data?.error) {
               throw new Error(error.response.data.error);
           }
           throw error;
       });
    }
}
